/**
 * The bridge between the stored shapes and mergeable items.
 *
 * `StoredPlan` is what the app and the domain engine read; a `SyncItem[]` is
 * what merges. Neither is a good substitute for the other, so this module
 * converts, and does nothing else — no I/O, no timestamps of its own.
 *
 * Round-tripping is the contract: `itemsToPlan(planToItems(p))` must equal `p`
 * for every plan, and there is a test that says so. Anything that fails to
 * round-trip is data this phase would silently drop.
 */

import type { CustomMeal, Library, StapleDefault } from '../store/library.js';
import type { ManualItem, StoredPlan } from '../store/db.js';
import type { AisleCategory, Store, Unit } from '../../domain/types.js';
import type { ItemMeta, SyncItem } from './merge.js';
import {
  PLAN_STORE_KEY, cookKey, dinnerKey, favKey, libStapleKey, lunchKey, manualKey, pantryKey,
  recipeKey, stapleKey, tickKey,
} from './keys.js';

export interface DinnerValue { mealId: string; spansDays: number; portions: number | null }
export interface LunchValue { componentIds: string[] }
export interface StapleValue { qty: number; unit: Unit }
export interface ManualValue { label: string; aisle: AisleCategory }
export interface FlagValue { on: boolean }
export interface StoreValue { store: Store }
export interface CookValue { rating: 'loved' | 'fine' | 'never' | null }
export interface LibStapleValue { qty: number; unit: Unit; usuallyOn: boolean }
export type RecipeValue = CustomMeal;

const FALLBACK: ItemMeta = { updatedAt: new Date(0).toISOString(), updatedBy: '' };

/**
 * An item whose meta is missing gets the epoch, so it loses every comparison
 * rather than winning them. A missing timestamp means "we do not know when this
 * happened", and the safe reading of that is "a long time ago" — the opposite
 * would let unknown provenance overwrite a known-recent edit.
 */
function metaFor(meta: Record<string, ItemMeta> | undefined, key: string): ItemMeta {
  return meta?.[key] ?? FALLBACK;
}

export function planToItems(plan: StoredPlan): SyncItem[] {
  const meta = plan.itemMeta;
  const items: SyncItem[] = [];
  const push = (key: string, value: unknown): void => {
    items.push({ key, value, meta: metaFor(meta, key) });
  };

  push(PLAN_STORE_KEY, { store: plan.store } satisfies StoreValue);

  for (const d of plan.dinners) {
    push(dinnerKey(d.date), {
      mealId: d.mealId, spansDays: d.spansDays, portions: d.portions,
    } satisfies DinnerValue);
  }
  for (const l of plan.lunches) {
    push(lunchKey(l.date, l.personId), { componentIds: [...l.componentIds] } satisfies LunchValue);
  }
  for (const s of plan.staples) {
    push(stapleKey(s.ingredientId), { qty: s.qty, unit: s.unit } satisfies StapleValue);
  }
  for (const m of plan.manual) {
    push(manualKey(m.id), { label: m.label, aisle: m.aisle } satisfies ManualValue);
  }
  for (const [id, have] of Object.entries(plan.pantry)) {
    push(pantryKey(id), { on: have } satisfies FlagValue);
  }
  for (const [k, ticked] of Object.entries(plan.ticked)) {
    push(tickKey(k), { on: ticked } satisfies FlagValue);
  }

  // Tombstones. These have no array entry by definition, so they are found
  // only in itemMeta — and they are the whole reason a delete survives a sync.
  for (const [key, m] of Object.entries(meta ?? {})) {
    if (m.deleted === true) items.push({ key, value: null, meta: m });
  }

  return items.sort((a, b) => a.key.localeCompare(b.key));
}

export function itemsToPlan(weekStart: string, fallbackStore: Store, items: SyncItem[]): StoredPlan {
  const plan: StoredPlan = {
    weekStart,
    store: fallbackStore,
    dinners: [], lunches: [], staples: [], manual: [],
    pantry: {}, ticked: {},
    updatedAt: new Date().toISOString(),
    itemMeta: {},
  };
  const meta: Record<string, ItemMeta> = {};

  for (const item of items) {
    meta[item.key] = item.meta;
    if (item.value === null || item.meta.deleted === true) continue;

    const colon = item.key.indexOf(':');
    const kind = item.key.slice(0, colon);
    const rest = item.key.slice(colon + 1);

    switch (kind) {
      case 'plan':
        if (rest === 'store') plan.store = (item.value as StoreValue).store;
        break;
      case 'dinner': {
        const v = item.value as DinnerValue;
        plan.dinners.push({ date: rest, mealId: v.mealId, spansDays: v.spansDays, portions: v.portions });
        break;
      }
      case 'lunch': {
        // `lunch:<date>:<personId>` — the date is fixed width, the person id is
        // whatever remains, so split on the FIRST colon only. A person id
        // containing a colon would otherwise silently truncate.
        const split = rest.indexOf(':');
        plan.lunches.push({
          date: rest.slice(0, split),
          personId: rest.slice(split + 1),
          componentIds: [...(item.value as LunchValue).componentIds],
        });
        break;
      }
      case 'staple': {
        const v = item.value as StapleValue;
        plan.staples.push({ ingredientId: rest, qty: v.qty, unit: v.unit });
        break;
      }
      case 'manual': {
        const v = item.value as ManualValue;
        plan.manual.push({ id: rest, label: v.label, aisle: v.aisle });
        break;
      }
      case 'pantry':
        plan.pantry[rest] = (item.value as FlagValue).on;
        break;
      case 'tick':
        plan.ticked[rest] = (item.value as FlagValue).on;
        break;
      default:
        // A key this version does not understand is kept in itemMeta but not
        // materialised. That way a newer device's data is preserved and pushed
        // back rather than being deleted by an older one.
        break;
    }
  }

  // The same orderings the reducer maintains, so a merged plan is
  // indistinguishable from a locally-edited one.
  plan.dinners.sort((a, b) => a.date.localeCompare(b.date));
  plan.lunches.sort((a, b) => a.date.localeCompare(b.date) || a.personId.localeCompare(b.personId));
  plan.itemMeta = meta;
  return plan;
}

export function libraryToItems(library: Library): SyncItem[] {
  const meta = library.itemMeta;
  const items: SyncItem[] = [];
  const push = (key: string, value: unknown): void => {
    items.push({ key, value, meta: metaFor(meta, key) });
  };

  for (const mealId of library.favourites) push(favKey(mealId), { on: true } satisfies FlagValue);
  for (const e of library.cookLog) {
    push(cookKey(e.mealId, e.cookedOn), { rating: e.rating } satisfies CookValue);
  }
  for (const s of library.staples) {
    push(libStapleKey(s.ingredientId), {
      qty: s.qty, unit: s.unit, usuallyOn: s.usuallyOn,
    } satisfies LibStapleValue);
  }
  for (const m of library.customMeals) push(recipeKey(m.id), { ...m } satisfies RecipeValue);
  for (const [key, m] of Object.entries(meta ?? {})) {
    if (m.deleted === true) items.push({ key, value: null, meta: m });
  }
  return items.sort((a, b) => a.key.localeCompare(b.key));
}

export function itemsToLibrary(items: SyncItem[], fallbackStaples: StapleDefault[]): Library {
  const favourites: string[] = [];
  const cookLog: Library['cookLog'] = [];
  const staples: StapleDefault[] = [];
  const customMeals: CustomMeal[] = [];
  const meta: Record<string, ItemMeta> = {};
  let sawStaple = false;

  for (const item of items) {
    meta[item.key] = item.meta;

    const colon = item.key.indexOf(':');
    const kind = item.key.slice(0, colon);
    const rest = item.key.slice(colon + 1);

    // A tombstoned staple still proves staples have been synced. Counting only
    // live ones would mean that deleting the last standing staple silently
    // restored all fourteen defaults on the next sync.
    if (kind === 'libstaple') sawStaple = true;

    if (item.value === null || item.meta.deleted === true) continue;

    switch (kind) {
      case 'fav':
        if ((item.value as FlagValue).on) favourites.push(rest);
        break;
      case 'cook': {
        const split = rest.lastIndexOf(':');
        cookLog.push({
          mealId: rest.slice(0, split),
          cookedOn: rest.slice(split + 1),
          rating: (item.value as CookValue).rating,
        });
        break;
      }
      case 'libstaple': {
        const v = item.value as LibStapleValue;
        staples.push({ ingredientId: rest, qty: v.qty, unit: v.unit, usuallyOn: v.usuallyOn });
        sawStaple = true;
        break;
      }
      case 'recipe':
        customMeals.push(item.value as RecipeValue);
        break;
      default:
        break;
    }
  }

  favourites.sort();
  cookLog.sort((a, b) => a.mealId.localeCompare(b.mealId) || a.cookedOn.localeCompare(b.cookedOn));
  staples.sort((a, b) => a.ingredientId.localeCompare(b.ingredientId));
  customMeals.sort((a, b) => a.id.localeCompare(b.id));

  return {
    favourites,
    cookLog,
    // An empty staple list is only meaningful if the incoming data actually had
    // staple keys. Otherwise this is a first sync against an account that has
    // never written any, and wiping the standing list would empty every future
    // shop without anyone asking for it.
    staples: sawStaple ? staples : fallbackStaples,
    customMeals,
    itemMeta: meta,
    updatedAt: new Date().toISOString(),
  };
}

/** Manual items are keyed by id; this is the reverse lookup the UI needs. */
export function manualById(plan: StoredPlan): Map<string, ManualItem> {
  return new Map(plan.manual.map((m) => [m.id, m]));
}
